import React, { useEffect, useState } from 'react';
import { Header, Loader } from '../../components';
import { capitalizeFirstLetterOfEachWord, customStyles } from '../../Utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { getDashboardData } from '../../rtk-apis/dashboard/getDashboardData';
import PieChartData from '../../components/PieChart';
import SplineChart from '../../components/LineChart';
import StudiesChart from '../../components/StudiesChart';
import ReportsBarChartComponent from '../../components/BarChart';
import CardiologistDashboard from './CardiologistDashboard';
import SonographerDashboard from './SonographerDashboard';
import AdminDashboard from './AdminDashboard';
import ReceptionistDashboard from './ReceptionistDashboard';
import SuperAdminDashboard from './SuperAdminDashboard';
import GroupAdminDashboard from '../groupAdmin/GroupAdminDashboard';

const Dashboard = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading } = useSelector((state: RootState) => state.getDashboardDataResponse);
  const role = localStorage.getItem('role') || sessionStorage.getItem('role');
  const isReader = role === 'Cardiologist' || role === 'Radiologist';
  const isSonographer = role === 'Sonographer';
  const isAdmin = role === 'Admin';
  const isReceptionist = role === 'Medical Receptionist';
  const isSuperAdmin = role === 'SUPER_ADMIN';
  const isGroupAdmin = role === 'Group Admin';
  const hasInsightsDashboard =
    isReader || isSonographer || isAdmin || isReceptionist || isSuperAdmin || isGroupAdmin;
  const [usersPerHospitalData, setUsersPerHospitalData] = useState<any[]>([]);
  const [usersPerRoleData, setUsersPerRoleData] = useState<any[]>([]);
  const [monthlyHospitalChartData, setMonthlyHospitalChartData] = useState({ categories: [], series: [] });
  const [studiesPerHospitalChartData, setStudiesPerHospitalChartData] = useState({ categories: [], series: [] });
  const [reportsByStatusData, setReportsByStatusData] = useState<any>({});
  const [showReportsGraph, setShowReportsGraph] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedStudyYear, setSelectedStudyYear] = useState<number>(new Date().getFullYear());

  const studyMonths = [
    'All', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // default to current month number as a string ("1".."12").
  // If you want default to "all", set initialSelectedMonth = "all";
  const initialSelectedMonth = String(new Date().getMonth() + 1); // e.g. October -> "10"
  const [selectedMonth, setSelectedMonth] = useState<string>(initialSelectedMonth);
  const [months, setMonths] = useState<string[]>([]); // kept if you still want to use API-returned months for something else

  const userEmail = localStorage.getItem('email') || sessionStorage.getItem('email');
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2021 + 1 }, (_, i) => 2021 + i);

  useEffect(() => {
    const fetchDashboardData = async () => {
      // Convert selectedMonth to payload form: number (1..12) or "all"
      const studyMonthNumber = selectedMonth === "all" ? "all" : Number(selectedMonth);

      const response = await dispatch(
        getDashboardData({
          year: selectedYear,
          loggedinEmail: userEmail,
          studyYear: selectedStudyYear,
          studyMonth: studyMonthNumber,
        })
      ).unwrap();

      if (response.status === "SUCCESS") {
        const { monthlyHospitals, usersPerHospital, usersPerRole, reportsByHospitalAndStatus, studiesPerHospital, monthFilter } = response.data;

        // setMonths (kept for compatibility); monthlyHospitals keys might be month names depending on backend
        setMonths(Object.keys(monthlyHospitals || {}));

        // Normalize monthFilter coming from backend into our component format ("all" or "1".."12")
        const normalizeMonthFilter = (mf: any) => {
          if (mf == null) return null;
          if (mf === 'all' || mf === 'All') return 'all';
          if (typeof mf === 'number') return String(mf);
          if (typeof mf === 'string') {
            const asNum = Number(mf);
            if (!isNaN(asNum)) return String(asNum); // backend may send "10"
            // backend may send month name like "October"
            const idx = studyMonths.indexOf(mf);
            if (idx > 0) return String(idx);
            return null;
          }
          return null;
        };

        const hasReportsData =
          reportsByHospitalAndStatus &&
          Object.keys(reportsByHospitalAndStatus).length > 0 &&
          Object.values(reportsByHospitalAndStatus).some(hospital =>
            Object.values(hospital).some(value => value > 0)
          );

        setShowReportsGraph(hasReportsData);

        const categories = Object.keys(monthlyHospitals || {});
        const seriesData = categories.map(month => monthlyHospitals[month]?.length || 0);
        setMonthlyHospitalChartData({ categories, series: [{ name: "Hospitals", data: seriesData }] });

        const usersPerRoleChart = usersPerRole?.map((roleData: any) => ({
          value: roleData.count,
          label: roleData.role,
          color: roleData.color,
        })) || [];
        setUsersPerRoleData(usersPerRoleChart);

        const usersPerHospitalChart = usersPerHospital?.map((hospitalData: any) => ({
          value: hospitalData.count,
          label: hospitalData.name,
          color: hospitalData.color,
        })) || [];
        setUsersPerHospitalData(usersPerHospitalChart);

        const studyCategories = studiesPerHospital?.map((hospital: any) => hospital.hospitalName) || [];
        const studySeriesData = studiesPerHospital?.map((hospital: any) => hospital.studiesCount) || [];

        setStudiesPerHospitalChartData({
          categories: studyCategories,
          series: [{ name: "Final Reports", data: studySeriesData }],
        });

        const firstHospital = Object.keys(reportsByHospitalAndStatus || {})[0];
        const hospitalReportsData = (reportsByHospitalAndStatus || {})[firstHospital] || {};
        setReportsByStatusData(hospitalReportsData);
      }
    };

    // Reader + Sonographer use dedicated insights dashboards — skip the legacy
    // graphData fetch for them.
    if (selectedMonth && !hasInsightsDashboard) {
      fetchDashboardData();
    }
  }, [dispatch, selectedYear, selectedStudyYear, selectedMonth, hasInsightsDashboard]);

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => setSelectedYear(Number(e.target.value));
  const handleStudyYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => setSelectedStudyYear(Number(e.target.value));
  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => setSelectedMonth(e.target.value);

  const renderDashboardContent = () => {
    const hasUsersPerRoleData = usersPerRoleData.length > 0;

    if (role !== 'SUPER_ADMIN' && !hasUsersPerRoleData && !showReportsGraph) {
      return (
        <div className="no-results">
          <p className="no-results-text">No Result Found!</p>
        </div>
      );
    }

    return (
      <>
        <div className={`cardRow${role === 'SUPER_ADMIN' ? ' cardRow__2' : ''}`}>
          {role === 'SUPER_ADMIN' && usersPerHospitalData.length > 0 && (
            <div className="col">
              <div className="card">
                <div className="card__header">
                  <h3 className="title">Users Per Hospital</h3>
                </div>
                <div className="card__body">
                  <div className="graphInfo">
                    <div className="graphInfo__graph">
                      <PieChartData chartData={usersPerHospitalData} />
                    </div>
                    <ul
                      className="graphInfo__info"
                      style={
                        usersPerHospitalData.length > 5
                          ? { maxHeight: "320px", overflowY: "auto" }
                          : {}
                      }
                    >
                      {usersPerHospitalData.map((data, i) => {
                        const fullLabel = capitalizeFirstLetterOfEachWord(data.label);
                        const truncatedLabel = fullLabel.length > 15 ? `${fullLabel.slice(0, 15)}...` : fullLabel;
                        return (
                          <li key={i}>
                            <div className="inline-flex items-center px-4 py-2 rounded-full w-full" style={{ backgroundColor: data.color }}>
                              <span className="text-white font-semibold mr-2" title={fullLabel}>{truncatedLabel + " - "}</span>
                              <span className="text-white">{data.value} {data.value === 1 ? "User" : "Users"}</span>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {role === 'SUPER_ADMIN' && usersPerRoleData.length > 0 && (
            <div className="col">
              <div className="card">
                <div className="card__header">
                  <h3 className="title">Users Per Role</h3>
                </div>
                <div className="card__body">
                  <div className={`graphInfo ${(role === 'Admin' || role === 'Sonographer') ? 'flex items-center' : ''}`}>
                    <div
                      className={`graphInfo__graph ${(role === 'Admin' || role === 'Sonographer') ? 'h-64 flex' : ''}`}
                      style={(role === 'Admin' || role === 'Sonographer') ? { maxWidth: '400px', height: '300px' } : undefined}
                    >
                      <PieChartData chartData={usersPerRoleData} />
                    </div>
                    <ul className={`graphInfo__info ${(role === 'Admin' || role === 'Sonographer') ? 'w-full' : ''}`}>
                      {usersPerRoleData.map((data, i) => (
                        <li key={i}>
                          <div
                            className="inline-flex items-center px-4 py-2 rounded-full w-full"
                            style={{ backgroundColor: data.color }}
                          >
                            <span className="text-white font-semibold mr-2">{capitalizeFirstLetterOfEachWord(data.label) + ' - '}</span>
                            <span className="text-white">{data.value} {data.value === 1 ? 'User' : 'Users'}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {role === 'SUPER_ADMIN' && studiesPerHospitalChartData.categories.length > 0 && (
          <div className="cardRow">
            <div className="col">
              <div className="card">
                <div className='flex justify-between items-center'>
                  <div className="card__header">
                    <h3 className="title">Final Reports Per Hospital</h3>
                  </div>
                  <div className="filter-container flex items-center">
                    <div className="year-filter mr-4">
                      <label htmlFor="year" className='font-bold mr-1 font-large'>Year: </label>
                      <select id="year" value={selectedStudyYear} onChange={handleStudyYearChange} className='bg-black border-2 p-1 cursor-pointer'>
                        {years.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                    <div className="month-filter">
                      <label htmlFor="month" className='font-bold mr-1 font-large'>Month: </label>
                      <select
                        id="month"
                        value={selectedMonth}
                        onChange={handleMonthChange}
                        className='bg-black border-2 p-1 cursor-pointer'
                      >
                        {studyMonths.map((m, idx) => (
                          <option key={m} value={idx === 0 ? "all" : String(idx)}>{m}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className="card__body">
                  <StudiesChart
                    categories={studiesPerHospitalChartData.categories}
                    series={studiesPerHospitalChartData.series}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {role === 'SUPER_ADMIN' && (
          <div className="cardRow">
            <div className="col">
              <div className="card">
                <div className='flex justify-between'>
                  <div className="card__header">
                    <h3 className="title">Hospitals Per Month</h3>
                  </div>
                  <div className="year-filter">
                    <label htmlFor="year" className='font-bold mr-1 font-large'>Year : </label>
                    <select id="year" className='bg-black border-2 p-1 cursor-pointer' value={selectedYear} onChange={handleYearChange}>
                      {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </div>
                <div className="card__body">
                  <SplineChart categories={monthlyHospitalChartData.categories} series={monthlyHospitalChartData.series} />
                </div>
              </div>
            </div>
          </div>
        )}

        {(role === 'Cardiologist' || role === 'Radiologist') && (
          showReportsGraph ? (
            <div className="col">
              <div className="card">
                <div className="card__header"><h3 className="title">Reports</h3></div>
                <div className="card__body">
                  <ReportsBarChartComponent reportsData={reportsByStatusData} />
                </div>
              </div>
            </div>
          ) : (
            <div className="no-results">
              <p className="no-results-text">No Result Found!</p>
            </div>
          )
        )}
      </>
    );
  };

  return (
    <>
      <section className="layoutContainer" style={customStyles.layoutBackground}>
        <div className="layoutGrid">
          <div className="page page--fullBody">
            <Header />
            <div className="page__body">
              {isSuperAdmin ? (
                <SuperAdminDashboard />
              ) : isGroupAdmin ? (
                <GroupAdminDashboard />
              ) : isReader ? (
                <CardiologistDashboard />
              ) : isSonographer ? (
                <SonographerDashboard />
              ) : isAdmin ? (
                <AdminDashboard />
              ) : isReceptionist ? (
                <ReceptionistDashboard />
              ) : (
                <div className="dashboardWrap">
                  <div className="dashboardWrap__body">
                    {renderDashboardContent()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      <Loader isLoading={loading} />
    </>
  );
};

export default Dashboard;
